import { v4 as uuidv4 } from 'uuid';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import { isPreOrder } from '../../../../products/utils/availability.utils';
import { OfflineSkuPriceHandler } from '../../../builders/utils/OfflineSkuPriceHandler.util';
import { OfflineDatabaseDeliveryWindow } from '../../../database/offlineDatabase/types/offlineDatabaseDeliveryWindow.types';
import {
  OfflineDatabaseOrderProduct,
  OfflineDatabaseOrderProductKit,
} from '../../../database/offlineDatabase/types/offlineDatabaseOrder.types';
import { OfflineDatabaseProduct } from '../../../database/offlineDatabase/types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from '../../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfo } from '../../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineOrderProductTransformers } from '../../../transformers/OfflineOrderProduct.transformers';
import {
  ChangeGridBasedOnPacksParams,
  OfflineDatabasePackWithMultiplier,
  OfflineGridItem,
  OfflineOrderProductCreateParams,
  OfflineOrderProductMetadata,
  OfflineOrderProductStaticCreateParams,
  OfflineOrderProductStaticCreateWithPacksParams,
} from './offlineOrderProduct.types';
import { getGridBasedOnPacks } from './utils/getGridBasedOnPacks/getGridBasedOnPacks';
import { OfflineOrderProductRulesFactory } from './validator/OfflineOrderProduct.factory';
import { OfflineOrderProductValidator } from './validator/OfflineOrderProduct.validator';
import { OfflineOrderProductStockError } from './validator/rules/ValidateStock.rule';

export class OfflineOrderProduct {
  private _grid: OfflineGridItem[] = [];

  private _createdAt: Date;
  private _product: OfflineDatabaseProduct;
  private _metadata: OfflineOrderProductMetadata;
  private _productColor: OfflineDatabaseProductColor;
  private _availability: AvailabilityEnum;

  private _kit?: OfflineDatabaseOrderProductKit;
  private _packs?: OfflineDatabasePackWithMultiplier[];
  private _deliveryWindow?: OfflineDatabaseDeliveryWindow;

  private constructor({
    kit,
    grid,
    packs,
    product,
    metadata,
    createdAt,
    availability,
    productColor,
    deliveryWindow,
  }: OfflineOrderProductCreateParams) {
    this._kit = kit;
    this._grid = grid;
    this._packs = packs;
    this._product = product;
    this._metadata = metadata;
    this._createdAt = createdAt;
    this._availability = availability;
    this._deliveryWindow = deliveryWindow;
    this._productColor = { ...productColor };
  }

  static fromOfflineDatabase(
    orderProductFromDatabase: OfflineDatabaseOrderProduct,
  ): OfflineOrderProduct {
    return new OfflineOrderProduct(orderProductFromDatabase);
  }

  static create(params: OfflineOrderProductStaticCreateParams): OfflineOrderProduct {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    const gridWithId = params.grid.map((gridItem) => ({
      ...gridItem,
      id: gridItem.id ?? uuidv4(),
    }));
    const formattedKit = OfflineOrderProductTransformers.offlineKitToOrderProductKit(params.kit);

    const sortedGrid = gridWithId.sort((a, b) => a.size.position - b.size.position);

    const offlineProduct = new OfflineOrderProduct({
      ...params,
      kit: formattedKit,
      grid: sortedGrid,
      createdAt: new Date(),
    });

    offlineProduct.cleanProductColorPacks();

    validator.validateOrThrow(offlineProduct, {
      useRulesTo: params.packs?.length ? 'CREATE_WITH_PACK' : 'CREATE',
    });

    return offlineProduct;
  }

  static createWithPacks(
    params: OfflineOrderProductStaticCreateWithPacksParams,
  ): OfflineOrderProduct {
    const { grid, packsWithMultiplier, respectStock, availability, productColor } = params;

    const gridWithQuantityOfPacks = getGridBasedOnPacks({
      grid,
      respectStock,
      availability,
      packsWithMultiplier,
    });

    try {
      const orderProduct = this.create({
        ...params,
        grid: gridWithQuantityOfPacks,
        packs: respectStock ? undefined : packsWithMultiplier,
      });

      return orderProduct;
    } catch (e) {
      const allowPartialApply = isPreOrder(availability)
        ? productColor.allowPackPartialApplyInPreOrder
        : productColor.allowPackPartialApplyInImmediate;

      if (e instanceof OfflineOrderProductStockError && !respectStock && allowPartialApply) {
        return this.createWithPacks({ ...params, respectStock: true });
      }

      throw e;
    }
  }

  changeGrid(gridValues: Array<Pick<OfflineGridItem, 'quantity' | 'id'>>) {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    this._packs = undefined;
    this.cleanProductColorPacks();

    gridValues.forEach(({ id, quantity }) => {
      const gridItem = this._grid.find((grid) => grid.id === id);

      if (!gridItem) {
        throw new Error('grid item not found');
      }

      gridItem.quantity = quantity;
    });

    validator.validateOrThrow(this, {
      useRulesTo: 'CHANGE_GRID',
    });
  }

  changeGridBasedOnPacks({
    packsWithMultiplier,
    productColorWithAllPacks,
  }: ChangeGridBasedOnPacksParams) {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    const gridWithQuantityOfPacks = getGridBasedOnPacks({
      grid: this._grid,
      packsWithMultiplier,
      availability: this._availability,
    });

    this._packs = packsWithMultiplier;
    this._grid = gridWithQuantityOfPacks;
    this._productColor.preOrderPacks = productColorWithAllPacks.preOrderPacks;
    this._productColor.immediatePacks = productColorWithAllPacks.immediatePacks;

    try {
      validator.validateOrThrow(this, {
        useRulesTo: 'CHANGE_GRID_WITH_PACK',
      });

      this.cleanProductColorPacks();
    } catch (e) {
      const allowPartialApply = isPreOrder(this._availability)
        ? this._productColor.allowPackPartialApplyInPreOrder
        : this._productColor.allowPackPartialApplyInImmediate;

      if (e instanceof OfflineOrderProductStockError && allowPartialApply) {
        const gridWithQuantityOfPacks = getGridBasedOnPacks({
          grid: this._grid,
          respectStock: true,
          packsWithMultiplier,
          availability: this._availability,
        });

        return this.changeGrid(gridWithQuantityOfPacks);
      }

      throw e;
    }
  }

  copyGrid(gridToCopy: OfflineGridItem[]) {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    this._grid.forEach((gridItem) => {
      const gridItemToCopy = gridToCopy.find(
        (gridItemToCopy) => gridItemToCopy.size.id === gridItem.size.id,
      );

      if (gridItemToCopy) {
        gridItem.quantity = gridItemToCopy.quantity;
      } else {
        gridItem.quantity = 0;
      }

      try {
        validator.validateOrThrow(this, {
          useRulesTo: 'CHANGE_GRID_TO_ITEM',
        });
      } catch (_) {
        gridItem.quantity = 0;
      }
    });

    validator.validateOrThrow(this, {
      useRulesTo: 'CHANGE_GRID',
    });
  }

  copyGridWithPacks({
    packsWithMultiplier,
    productColorWithAllPacks,
  }: ChangeGridBasedOnPacksParams) {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    const gridWithQuantityOfPacks = getGridBasedOnPacks({
      grid: this._grid,
      packsWithMultiplier,
      availability: this._availability,
    });

    this._packs = packsWithMultiplier;
    this._grid = gridWithQuantityOfPacks;
    this._productColor.preOrderPacks = productColorWithAllPacks.preOrderPacks;
    this._productColor.immediatePacks = productColorWithAllPacks.immediatePacks;

    this.cleanProductColorPacks();

    try {
      validator.validateOrThrow(this, {
        useRulesTo: 'CHANGE_GRID_WITH_PACK',
      });
    } catch (_) {
      const gridWithZeroQuantity = this._grid.map((gridItem) => ({
        ...gridItem,
        quantity: 0,
      }));

      this._packs = undefined;
      this._grid = gridWithZeroQuantity;

      this.cleanProductColorPacks();

      validator.validateOrThrow(this, {
        useRulesTo: 'CHANGE_GRID_WITH_PACK',
      });
    }
  }

  changePrice(newPriceTable: OfflineDatabaseProductColorPriceInfo) {
    const validator = new OfflineOrderProductValidator(new OfflineOrderProductRulesFactory());

    this._metadata.productColorPriceInfo = newPriceTable;

    validator.validateOrThrow(this, {
      useRulesTo: 'CHANGE_PRICE_TABLE',
    });
  }

  getTotalGridQuantity() {
    return this._grid.reduce((acc, gridItem) => acc + gridItem.quantity, 0);
  }

  private cleanProductColorPacks() {
    const preOrderPacks = isPreOrder(this._availability)
      ? this._productColor.preOrderPacks?.filter((pack) =>
          this._packs?.some((appliedPacks) => appliedPacks.pack.id === pack.packId),
        )
      : undefined;
    const immediatePacks = isPreOrder(this._availability)
      ? undefined
      : this._productColor.immediatePacks?.filter((pack) =>
          this._packs?.some((appliedPacks) => appliedPacks.pack.id === pack.packId),
        );

    this._productColor.preOrderPacks = preOrderPacks;
    this._productColor.immediatePacks = immediatePacks;
  }

  get grid() {
    return this._grid;
  }

  get packs() {
    return this._packs;
  }

  get product() {
    return this._product;
  }

  get availability() {
    return this._availability;
  }

  get productColor() {
    return this._productColor;
  }

  get deliveryWindow() {
    return this._deliveryWindow;
  }

  get metadata() {
    return this._metadata;
  }

  get createdAt() {
    return this._createdAt;
  }

  get kit() {
    return this._kit;
  }

  get retailPrice() {
    const totalRetailPrice = this._grid.reduce((gridAcc, gridItem) => {
      const { retailPrice } = OfflineSkuPriceHandler.getPricesBySize(
        this._metadata.productColorPriceInfo,
        gridItem.size.id,
      );

      const safeRetailPrice = retailPrice ?? 0;

      return gridAcc + gridItem.quantity * safeRetailPrice;
    }, 0);
    return totalRetailPrice;
  }

  get grossValue() {
    const grossValue = this._grid.reduce((gridAcc, gridItem) => {
      const { wholesalePrice } = OfflineSkuPriceHandler.getPricesBySize(
        this._metadata.productColorPriceInfo,
        gridItem.size.id,
      );

      const safeWholesalePrice = wholesalePrice ?? 0;

      return gridAcc + gridItem.quantity * safeWholesalePrice;
    }, 0);
    return grossValue;
  }

  get subTotal() {
    const subTotal = this._grid.reduce((gridAcc, gridItem) => {
      const { unitPrice } = OfflineSkuPriceHandler.getPricesBySize(
        this._metadata.productColorPriceInfo,
        gridItem.size.id,
      );

      const safeValue = unitPrice ?? 0;

      return gridAcc + gridItem.quantity * safeValue;
    }, 0);

    return subTotal;
  }
}
